/**
 * 營銷追蹤工具
 * 整合 GA4 和 Meta Pixel
 */

// GA4 配置
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
    dataLayer: unknown[]
    fbq: (...args: unknown[]) => void
    _fbq: unknown
  }
}

// 追蹤配置
interface AnalyticsConfig {
  ga4Id?: string
  metaPixelId?: string
  debug?: boolean
}

let config: AnalyticsConfig = {}

/**
 * 初始化追蹤服務
 */
export const initAnalytics = (options: AnalyticsConfig) => {
  config = options

  // 初始化 GA4
  if (config.ga4Id) {
    initGA4(config.ga4Id)
  }

  // 初始化 Meta Pixel
  if (config.metaPixelId) {
    initMetaPixel(config.metaPixelId)
  }

  // 追蹤 UTM 參數
  trackUTMParams()
}

/**
 * 初始化 Google Analytics 4
 */
const initGA4 = (measurementId: string) => {
  // 載入 gtag.js
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  // 初始化 dataLayer
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId)

  if (config.debug) {
    console.log('[Analytics] GA4 initialized:', measurementId)
  }
}

/**
 * 初始化 Meta Pixel
 */
const initMetaPixel = (pixelId: string) => {
  // Meta Pixel 初始化代碼
  interface FBQ {
    (...args: unknown[]): void
    callMethod?: (...args: unknown[]) => void
    queue: unknown[]
    loaded: boolean
    version: string
    push: (...args: unknown[]) => void
  }

  const fbq: FBQ = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
    } else {
      fbq.queue.push(args)
    }
  } as FBQ

  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.push = (...args: unknown[]) => fbq.queue.push(args)

  window.fbq = fbq
  if (!window._fbq) window._fbq = fbq

  // 載入 Meta Pixel SDK
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  window.fbq('init', pixelId)
  window.fbq('track', 'PageView')

  if (config.debug) {
    console.log('[Analytics] Meta Pixel initialized:', pixelId)
  }
}

/**
 * 追蹤頁面瀏覽
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  // GA4
  if (config.ga4Id && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle
    })
  }

  // Meta Pixel
  if (config.metaPixelId && window.fbq) {
    window.fbq('track', 'PageView')
  }

  if (config.debug) {
    console.log('[Analytics] Page view:', pagePath)
  }
}

/**
 * 追蹤自定義事件
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
) => {
  // GA4
  if (config.ga4Id && window.gtag) {
    window.gtag('event', eventName, eventParams)
  }

  // Meta Pixel - 映射常見事件
  if (config.metaPixelId && window.fbq) {
    const metaEventMap: Record<string, string> = {
      registration_start: 'InitiateCheckout',
      registration_complete: 'CompleteRegistration',
      checkin_complete: 'Lead',
      course_view: 'ViewContent'
    }

    const metaEvent = metaEventMap[eventName] || eventName
    window.fbq('track', metaEvent, eventParams)
  }

  if (config.debug) {
    console.log('[Analytics] Event:', eventName, eventParams)
  }
}

/**
 * 追蹤報名開始
 */
export const trackRegistrationStart = (courseId: number, courseName: string) => {
  trackEvent('registration_start', {
    course_id: courseId,
    course_name: courseName,
    currency: 'TWD'
  })
}

/**
 * 追蹤報名完成
 */
export const trackRegistrationComplete = (
  courseId: number,
  courseName: string,
  value?: number
) => {
  trackEvent('registration_complete', {
    course_id: courseId,
    course_name: courseName,
    value: value,
    currency: 'TWD'
  })
}

/**
 * 追蹤簽到完成
 */
export const trackCheckinComplete = (eventId: number, eventName: string) => {
  trackEvent('checkin_complete', {
    event_id: eventId,
    event_name: eventName
  })
}

/**
 * UTM 參數處理
 */
interface UTMParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

/**
 * 解析 URL 中的 UTM 參數
 */
export const parseUTMParams = (): UTMParams => {
  const params = new URLSearchParams(window.location.search)
  const utmParams: UTMParams = {}

  const utmKeys: (keyof UTMParams)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ]

  utmKeys.forEach(key => {
    const value = params.get(key)
    if (value) {
      utmParams[key] = value
    }
  })

  return utmParams
}

/**
 * 追蹤並存儲 UTM 參數
 */
export const trackUTMParams = () => {
  const utmParams = parseUTMParams()

  if (Object.keys(utmParams).length > 0) {
    // 存儲到 sessionStorage
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams))

    // 發送到 GA4
    if (config.ga4Id && window.gtag) {
      window.gtag('set', 'campaign', utmParams)
    }

    if (config.debug) {
      console.log('[Analytics] UTM params:', utmParams)
    }
  }
}

/**
 * 獲取存儲的 UTM 參數
 */
export const getStoredUTMParams = (): UTMParams => {
  const stored = sessionStorage.getItem('utm_params')
  return stored ? JSON.parse(stored) : {}
}

/**
 * 獲取流量來源摘要
 */
export const getTrafficSource = (): string => {
  const utmParams = getStoredUTMParams()

  if (utmParams.utm_source) {
    return `${utmParams.utm_source}${utmParams.utm_medium ? ` / ${utmParams.utm_medium}` : ''}`
  }

  // 檢查 referrer
  const referrer = document.referrer
  if (referrer) {
    try {
      const url = new URL(referrer)
      return url.hostname
    } catch {
      return 'direct'
    }
  }

  return 'direct'
}

export default {
  initAnalytics,
  trackPageView,
  trackEvent,
  trackRegistrationStart,
  trackRegistrationComplete,
  trackCheckinComplete,
  parseUTMParams,
  getStoredUTMParams,
  getTrafficSource
}
